<template>
    <div v-show="false">
        <div ref="extraModes" class="extra-modes">
            <button
                @click="enterDeleteMode(); ebxLogAnalytics(GA_EVENTS.BTN_DELETE_POLYGON);"
                role="button"
                title="Delete polygon"
                aria-label="Delete polygon"
                aria-pressed="false"
                draggable="false"
                class="extra-mode-button"
                :class="{ selected: drawingMode === 'delete' }"
            >
                <md-icon :class="{ 'md-primary': drawingMode === 'delete' }"
                    >delete_outline</md-icon
                >
            </button>
            <button
                @click="enterAssignMode(); ebxLogAnalytics(GA_EVENTS.BTN_ASSIGN_AREA);"
                role="button"
                title="Assign area"
                aria-label="Assign area"
                aria-pressed="false"
                draggable="false"
                class="extra-mode-button"
                :class="{ selected: drawingMode === 'assign' }"
            >
                <md-icon :class="{ 'md-primary': drawingMode === 'assign' }"
                    >brush</md-icon
                >
            </button>
            <button
                v-if="showLineDownload && showLineDrawing"
                @click="enterDownloadMode()"
                role="button"
                title="Download Line"
                aria-label="Download Line"
                aria-pressed="false"
                draggable="false"
                class="extra-mode-button"
                :class="{ 'selected': drawingMode === 'download' }"
            >
                <md-icon :class="{ 'md-primary': drawingMode === 'assign' }">get_app</md-icon>
            </button>
            <button
                v-if="showMapMeasurement"
                @click="toggleMapMeasurementMode()"
                role="button"
                title="Map measurement tools"
                aria-label="Map measurement tools"
                aria-pressed="false"
                draggable="false"
                class="extra-mode-button"
            >
                <md-icon
                    :class="{ 'md-primary': mapMeasureMode }"
                >
                straighten
                </md-icon>
            </button>
            <GoogleMapPolygonUpload
                v-if="showMapPolygonUpload"
                :google="google"
                :map="map"
                :selected-area="selectedArea"
                :mapMeasureMode="mapMeasureMode"
                @polygon-clicked="polygonClicked"
                @add-area="addArea"
                data-html2canvas-ignore="true"
                @update-latlng="(value) => $emit('update-latlng', value)"
                @disable-polygon="(value) => disabledPolygons.push(value)"
                @set-polygon-listeners="(value) => setPolygonListeners(value)"
                @set-overlay-listeners="(polygon, type) => setOverlayListeners(polygon, type)"
            />
        </div>
        <md-snackbar
            :md-position="'center'"
            :md-duration="10000"
            :md-active.sync="snackbar.visible"
            md-persistent
        >
            <span>{{ snackbar.message }}</span>
        </md-snackbar>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
import { AreaService } from "@/services/area.service";
import GoogleMapPolygonUpload from "@/components/ResultMap/GoogleMapPolygonUpload";
import MeasureTool from 'measuretool-googlemaps-v3';
import { AuthService } from "@/services/auth.service";
import { ConfigService } from '@/services/config.service';
import * as configConstants from "@/config/constants";
import { OrgConfig } from "@/config/main";

export default {
    components: {
        GoogleMapPolygonUpload,
    },
    props: {
        google: {
            type: Object,
            required: true,
        },
        map: {
            type: Object,
            required: true,
        },
        selectedArea: {
            type: Object,
            required: true,
        },
        drawingIsLocked: {
            type: Boolean,
            required: true
        },
        showLockControls: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            selectedPolygon: null,
            drawingMode: null,
            snackbar: {
                visible: false,
                message: "",
            },
            isAreaLimited: false, // enable/disable limited area size
            maxAreaSize: 100000, // max area size in km squared
            dirtyShapes: [],
            mapMeasureMode: false,
            measureTool: null,
            disabledPolygons: [],
            user: null,
            orgConfig: null,
            polylines: []
        };
    },
    watch: {
        showLockControls(newValue) {
            if (newValue === false) {
                this.drawingManager.setDrawingMode(null);
            }
        },
        selectedArea(newArea) {
            this.drawingManager.setOptions({
                polygonOptions: {
                    strokeColor: newArea.colour,
                },
                rectangleOptions: {
                    strokeColor: newArea.colour,
                },
            });
        },
        dirtyShapes(dirtyShapes) {
            if(dirtyShapes.length > 0) {
                this.$emit('disable-run', true);
            } else {
                this.$emit('disable-run', false);
            }
        },
        showLineDrawing(showLineDrawing){
            let drawingModesArray = ["polygon", "rectangle"];
            if (showLineDrawing === true ) {
                drawingModesArray = ["polygon", "rectangle", this.google.maps.drawing.OverlayType.POLYLINE];
            }
            this.drawingManager.drawingControlOptions.drawingModes = drawingModesArray;
        }
    },
    methods: {
        enforceDrawingLock() {
            if (!this.drawingIsLocked) {
                this.drawingManager.setDrawingMode(null);
                this.$emit("hide-lock-controls")
            }
        },
        addArea(areaName) {
            this.$emit('add-area', areaName);
        },
        setListeners() {
            let addListener = this.google.maps.event.addListener;

            addListener(this.drawingManager, "drawingmode_changed", () => {
                this.drawingMode = this.drawingManager.getDrawingMode();
                this.map.setOptions({ draggableCursor: "" });
                if (this.selectedPolygon) {
                    this.selectedPolygon.setEditable(false);
                }
                // need to emit the drawing mode for the lock mode controls, excluding polyline as that's weirdly only for line downloads
                if (this.drawingMode !== null && this.drawingMode !== "polyline") {
                    this.$emit('drawing-mode-entered', this.drawingMode)
                } else {
                    this.$emit("hide-lock-controls")
                }
            });

            addListener(this.map, "rightclick", () => {
                this.drawingManager.setDrawingMode(null);
            });

            addListener(this.drawingManager, "overlaycomplete", (event) => {
                let type = event.type;
                let overlay = event.overlay;
                let selectedArea = this.selectedArea;
                let customAreas = AreaService.getCustomAreas();

                if (type != 'polyline'){  //if type is not polyline 
                    if(customAreas.length === 0) { // if custom areas equal to 0 
                        this.$emit('add-area'); 
                        customAreas = AreaService.getCustomAreas();
                        selectedArea = {
                            name: customAreas[0],
                            colour: AreaService.getColour(customAreas[0])
                        }
                    }
                    overlay.setOptions({
                        fillOpacity: 0,
                        strokeWeight: 4,
                        strokeColor: selectedArea.colour,
                    });

                    // setting overlay areaID, this is used for 
                    overlay.areaId = selectedArea.name;
                    AreaService.addUserDrawnShape(selectedArea.name, overlay);
                } else {
                    this.polylines.push(overlay)
                }
                this.setOverlayListeners(overlay, type);               
            });

            addListener(this.drawingManager, "rectanglecomplete", (rectangle) => {
                this.setRectangleListeners(rectangle);
                this.enforceDrawingLock()
            });

            addListener(this.drawingManager, "polygoncomplete", (polygon) => {
                this.setPolygonListeners(polygon)
                this.enforceDrawingLock()
            });
        },
        setOverlayListeners(overlay, type){
            let addListener = this.google.maps.event.addListener;

            if (type != 'polyline'){
                addListener(overlay, "click", () =>
                    this.polygonClicked(overlay)
                );
            } else {
                var line = overlay;
                addListener(line, "click", () => this.lineClicked(line));

            }
            addListener(overlay, "mousemove", (event) =>
                this.$emit("update-latlng", event.latLng)
            );
            addListener(overlay, "mouseover", () => {
                if (this.mapMeasureMode) {
                    // disables polygon to allow measurement tools to work
                    overlay.setOptions({clickable: false});
                    this.disabledPolygons.push(overlay)
                }
            });
        },
        setRectangleListeners(rectangle) {
            let addListener = this.google.maps.event.addListener;
            this.validateRectangle(rectangle);

            addListener(rectangle, "bounds_changed", () => {
                this.validateRectangle(rectangle);
                AreaService.publishShapeUpdate({
                    areaId: rectangle.areaId,
                    shapeId: rectangle.shapeId,
                });
            });
        },
        setPolygonListeners(polygon) {
            let addListener = this.google.maps.event.addListener;
            let path = polygon.getPath();
            this.validatePolygon(polygon);

            addListener(path, "insert_at", () => {
                this.validatePolygon(polygon);
                AreaService.publishShapeUpdate({
                    areaId: polygon.areaId,
                    shapeId: polygon.shapeId,
                });
            });
            addListener(path, "set_at", () => {
                this.validatePolygon(polygon);
                AreaService.publishShapeUpdate({
                    areaId: polygon.areaId,
                    shapeId: polygon.shapeId,
                });
            });
            addListener(path, "remove_at", () => {
                this.validatePolygon(polygon);
                AreaService.publishShapeUpdate({
                    areaId: polygon.areaId,
                    shapeId: polygon.shapeId,
                });
            });
        },
        polygonClicked(polygon) {
            if (this.drawingMode == "delete") {
                this.deleteShape(polygon);
                return;
            }

            if (this.drawingMode == "assign") {
                polygon.setOptions({
                    strokeColor: this.selectedArea.colour,
                });

                polygon = AreaService.updateAreaIdOfShape(polygon.shapeId, this.selectedArea.name, polygon);

                return;
            }

            if (this.selectedPolygon) {
                this.selectedPolygon.setEditable(false);
            }
            this.selectedPolygon = polygon;
            this.selectedPolygon.setEditable(true);
        },
        lineClicked(line) {
            if (this.drawingMode == "delete") {
                line.setMap(null);
                return;
            } else if (this.drawingMode == "download") {
                let polylinePath = line.getPath();
                    let coordinates = [];
                    for (let i = 0; i < polylinePath.length; i++) {
                        coordinates.push({
                            lat: polylinePath.getAt(i).lat(),
                            lng: polylinePath.getAt(i).lng()
                        });
                    }
                let formattedCoords = coordinates.map(c => [c.lng,c.lat]);

                let json = {
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: "LineString",
                        coordinates: formattedCoords
                    }
                }
                let string = JSON.stringify(json);
                let dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(string);
                let downloadAnchorNode = document.createElement('a');
                downloadAnchorNode.setAttribute("href", dataStr);
                let fileName = prompt('Please name the file being downloaded:')
                if (fileName === null){null}
                else if (fileName != ''){
                    downloadAnchorNode.setAttribute("download", fileName + ".geojson");
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                } else if (fileName == ''){
                    downloadAnchorNode.setAttribute("download", "lineExport.geojson");
                    document.body.appendChild(downloadAnchorNode);
                    downloadAnchorNode.click();
                    downloadAnchorNode.remove();
                }
            }
        },
        toggleMapMeasurementMode() {
            if (!this.mapMeasureMode && this.showMapMeasurement === true) {
                this.enterMapMeasure();
            } else if (this.mapMeasureMode) {
                this.exitMapMeasure();
            }
        },
        enterMapMeasure() {
            this.measureTool.start();
            this.map.setOptions({ draggableCursor: "crosshair" });
            this.mapMeasureMode = true;
        },
        exitMapMeasure() {
            this.measureTool.end();
            this.map.setOptions({ draggableCursor: "default" });
            this.mapMeasureMode = false;
            // re-enables all polygons disabled when using measurement tools
            this.disabledPolygons.forEach(polygon => {
                polygon.setOptions({clickable: true});
            })
            // clears disabled array
            this.disabledPolygons = []
        },
        enterDeleteMode() {
            this.drawingManager.setDrawingMode(null);
            this.drawingMode = "delete";
            this.map.setOptions({ draggableCursor: "pointer" });
        },
        enterAssignMode() {
            this.drawingManager.setDrawingMode(null);
            this.drawingMode = "assign";
            this.map.setOptions({ draggableCursor: "alias" });
        },
        enterDownloadMode() {
            if (this.showLineDownload === true && this.showLineDrawing) {
                this.drawingManager.setDrawingMode(null);
                this.drawingMode = "download";
                this.map.setOptions({ draggableCursor: "pointer" });
            }
        },
        validatePolygon(polygon) {
            var area = this.calcPolygonArea(polygon);
            // get index from object ref
            let index = this.dirtyShapes.indexOf(polygon);

            if (area > this.maxAreaSize && this.isAreaLimited) {
                this.snackbar.message =
                    "Polygon area must be less than 10000 square kilometers on the Trial version, your polygon was " +
                    area.toFixed(2);
                this.snackbar.visible = true;
                if (index === -1) {
                    this.dirtyShapes.push(polygon);
                }
            } else {
                if (index !== -1) {
                    this.dirtyShapes.splice(index, 1);
                }
            }
        },
        validateRectangle(rectangle) {
            var area = this.calcRectangleArea(rectangle);
            // get index from object ref
            let index = this.dirtyShapes.indexOf(rectangle);

            if (area > this.maxAreaSize && this.isAreaLimited) {
                this.snackbar.message =
                    "Rectangle area must be less than 10000 square kilometers on the Trial version, your rectangle was " +
                    area.toFixed(2);
                this.snackbar.visible = true;
                if (index === -1) {
                    this.dirtyShapes.push(rectangle);
                }
            } else {
                if (index !== -1) {
                    this.dirtyShapes.splice(index, 1);
                }
            }
        },
        calcRectangleArea(rectangle) {
            var bounds = rectangle.getBounds();
            var NE = bounds.getNorthEast();
            var SW = bounds.getSouthWest();
            var NW = new this.google.maps.LatLng(NE.lat(), SW.lng());
            var SE = new this.google.maps.LatLng(SW.lat(), NE.lng());

            var latLngList = [NE, NW, SW, SE];

            // calculates area based on radius of earth
            var area = this.google.maps.geometry.spherical.computeArea(
                latLngList
            );
            area = area / 1e6; // converting to km^2

            return area;
        },
        calcPolygonArea(polygon) {
            // calculates area based on radius of earth
            var area = this.google.maps.geometry.spherical.computeArea(
                polygon.getPath()
            );
            area = area / 1e6; // converting to km^2

            return area;
        },
        deleteShape(shape) {
            let index = this.dirtyShapes.indexOf(shape);
            if(index !== -1) {
                this.dirtyShapes.splice(index, 1);
            }

            AreaService.removeShapeByID(shape.shapeId);
            return;
        },
    },
    mounted() {
        const { DrawingManager } = this.google.maps.drawing;

        this.subscription = AuthService.loggedUser$.subscribe(
            (user) => (this.user = user)
        );

        ConfigService.getOrgConfig(this.user.orgId).then(function(config){
            this.orgConfig = config;
        }.bind(this));

        let drawingModesArray = ["polygon", "rectangle"];
        if (this.showLineDrawing === true) {
            drawingModesArray = ["polygon", "rectangle", this.google.maps.drawing.OverlayType.POLYLINE];
        }

        this.drawingManager = new DrawingManager({
            drawingMode: null,
            drawingControl: true,
            drawingControlOptions: {
                position: this.google.maps.ControlPosition.TOP_LEFT,
                drawingModes: drawingModesArray,
            },
            polygonOptions: {
                editable: false,
                fillOpacity: 0.05,
            },
            rectangleOptions: {
                editable: false,
                fillOpacity: 0.05,
                strokeWeight: 3,
            },
            polylineOptions: {
                editable: true,
                fillOpacity: 0.05,
                strokeWeight: 1.5
            },
        });

        this.setListeners();

        this.drawingManager.setMap(this.map);

        const extraModes = this.$refs.extraModes;
        this.map.controls[this.google.maps.ControlPosition.TOP_LEFT].push(
            extraModes
        );

        this.measureTool = new MeasureTool(this.map, {
            showSegmentLength: true,
            contextMenu: false
        });

        this.clearAreasSubscription = AreaService.clearAreas$.subscribe(() => {
            this.polylines.forEach(polyline => {
                polyline.setMap(null);
            })
        })
    },
    beforeDestroy() {
        this.subscription.unsubscribe();
        this.clearAreasSubscription.unsubscribe();
    },
    computed: {
        showMapPolygonUpload() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.POLYGON_UPLOAD);
        },
        showMapMeasurement() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.MAP_MEASUREMENT);
        },
        showLineDownload() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.LINE_DRAWING_DOWNLOAD);
        },
        showLineDrawing() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.MAP_LINE_DRAWING);
        }
    },
};
</script>

<style lang="scss" scoped>
.extra-modes {
    float: left;
    line-height: 0;
    margin-top: 5px;
    margin-left: -5px;
}

.extra-modes > div {
    display: inline-block;
}

.extra-modes .md-icon {
    width: 19px;
    height: 19px;
}

.extra-mode-button {
    direction: ltr;
    overflow: hidden;
    text-align: left;
    position: relative;
    color: rgb(86, 86, 86);
    user-select: none;
    font-size: 11px;
    background-color: rgb(255, 255, 255);
    padding: 4px 0;
    border-bottom-right-radius: 2px;
    border-top-right-radius: 2px;
    background-clip: padding-box;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
    border: 0px;

    &:hover, &:focus {
        background-color: #ebebeb;
    }
}

.gmnoprint > div > div[aria-pressed="true"][title="Draw a shape"],
.gmnoprint > div > div[aria-pressed="true"][title="Draw a rectangle"],
.gmnoprint > div > div[aria-pressed=true][title="Draw a line"],
.extra-mode-button.selected {
    filter: saturate(1000%) hue-rotate(0.1turn);
}
</style>
